import { useReactiveVar } from '@apollo/client'
import { Trans } from '@lingui/macro'
import * as React from 'react'

import { currencyCode } from '@emico/currency-code'
import { CurrencyEnum } from '@emico/graphql-schema-types'

import styles from './PriceFilter.module.scss'
import Label from '../../../../input/Label/Label'
import { BaseSlider } from '../../../../input/Slider/Slider'

type Props = Omit<React.ComponentProps<typeof BaseSlider>, 'initialValue'>

export const fixMax = (min: number, max: number, step: number) =>
    Math.ceil((max - min) / step) * step + min

export const fixMin = (min: number, step: number) =>
    Math.floor(min / step) * step

const PriceFilter = ({ min, max, step = 5, value, onChange }: Props) => {
    const currency = useReactiveVar(currencyCode)
    // The slider requires the min and max value to be divisible by step
    const fixedMin = fixMin(min, step)
    const fixedMax = fixMax(fixedMin, max, step)

    // Make sure initial value is within sliders min-max range
    const initialValue =
        value === undefined
            ? [fixedMin, fixedMax]
            : [
                  fixMin(value[0], step),
                  fixMax(fixMin(value[0], step), value[1], step),
              ]

    const [currentValue, setCurrentValue] = React.useState(initialValue)

    const handleChange = (value: number[]) => {
        setCurrentValue(value)
    }

    return (
        <>
            <Label field="price" className={styles.label}>
                <Trans id="catalog.priceFilter.priceRange">Price Range</Trans>
            </Label>
            <BaseSlider
                initialValue={[fixedMin, fixedMax]}
                min={fixedMin}
                max={fixedMax}
                step={step}
                // use a fallback for the value so that resetting correctly updates
                // the state.
                value={currentValue}
                onChange={handleChange}
                onAfterChange={onChange}
                className={styles.slider}
                currency={currency as CurrencyEnum}
            />
        </>
    )
}

export default PriceFilter
